import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Dialog, DialogActions, DialogContent, Button, CircularProgress, Typography } from '@mui/material';
import Fade from '@mui/material/Fade';
import Stack from '@mui/material/Stack';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as Sentry from '@sentry/browser';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';

import api, { getUserTimeZone } from '../Services/Api';
import CRMObjectForm from './components/CRMObjectForm';
import ReviewStepV2 from './components/ReviewStepV2';
import ValidationErrorBoundary from './components/ValidationErrorBoundary';
import ProcessingComponent from '../designsystem/ProcessingComponent';
import { saveDraft, clearDraft } from './components/DraftBanner';
import { getLanguage } from '../Services/i18n';
import { ACCENT, TEXT_PRIMARY, TEXT_SECONDARY, DIALOG_PAPER } from '../designsystem/tokens';
import { canonicalObjectType, isAllowlistedProperty, shouldKeepCustomProperty } from './propertyAllowlist';

import BusinessIcon from '@mui/icons-material/Business';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import EmailIcon from '@mui/icons-material/Email';
import NoteIcon from '@mui/icons-material/Note';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const ENTITY_ICONS = {
    deal: <HandshakeIcon sx={{ fontSize: 18, color: ACCENT }} />,
    contact: <PersonIcon sx={{ fontSize: 18, color: ACCENT }} />,
    company: <BusinessIcon sx={{ fontSize: 18, color: ACCENT }} />,
    task: <TaskAltIcon sx={{ fontSize: 18, color: ACCENT }} />,
    meeting: <EventIcon sx={{ fontSize: 18, color: ACCENT }} />,
    email: <EmailIcon sx={{ fontSize: 18, color: ACCENT }} />,
    note: <NoteIcon sx={{ fontSize: 18, color: ACCENT }} />,
    call: <PhoneIcon sx={{ fontSize: 18, color: ACCENT }} />,
    REVIEW: <CheckCircleIcon sx={{ fontSize: 18, color: ACCENT }} />,
};

/**
 * ValidateV2 — clean validation flow built entirely on CRMObject[].
 *
 * State is a simple CRMObject[] array.
 * Each step = one CRMObject rendered via CRMObjectForm.
 * Final step = Review showing all objects via ReviewStepV2.
 *
 * Socket events:
 *   - "actions" → populate CRMObject[] shells (legacy compat)
 *   - "actionsUpdate" → merge enriched data into CRMObject by type
 *   - Express mode jumps to review when all mandatory fields filled
 */
function ValidateV2() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { setHide, user } = useOutletContext();
    const { t } = useTranslation();
    const text = state ? state.text : '';
    const ocrText = state ? state.ocrText : '';
    const oneShot = state ? state.oneShot : false;
    // Entry was launched from a contact detail "Voice Update" — lock the contact
    // step to edit mode so neither the AI nor the user can flip it to create/select.
    const editContactLock = state?.mode === 'edit-contact';

    const [processingData, setProcessingData] = useState(true);
    const [titleProcessing, setTitleProcessing] = useState(t('identifyingActions'));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [nextDisabled, setNextDisabled] = useState(false);
    const [open, setOpen] = useState(false);
    const [renderKey, setRenderKey] = useState(0);
    const [processingSteps, setProcessingSteps] = useState([]);
    const [oneShotMode, setOneShotMode] = useState(false);
    const [routeBack, setRouteBack] = useState('/app');

    // CRMObject[] state
    const [objects, setObjects] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [textSent, setTextSent] = useState(false);

    const objectsRef = useRef([]);
    const processedObjectIds = useRef(new Set());
    const oneShotChecked = useRef(false);
    const flowIdRef = useRef(state?.flowId || generateFlowId());
    const receivedObjectsEvent = useRef(false);
    const currentStepRef = useRef(0);
    const aiSnapshotsRef = useRef(new Map());
    // Snapshot of the original contact when entering via edit-contact lock.
    // Used to skip the contact step when AI extracts nothing new to edit.
    const lockedContactSnapshotRef = useRef(null);
    const socket = api.socket;

    const logFlowEvent = useCallback((event, extra = {}) => {
        const payload = { event, flowId: flowIdRef.current, ...extra };
        Sentry.addBreadcrumb({ category: 'validate-v2', level: 'info', message: event, data: payload });
        console.info('[ValidateV2]', payload);
    }, []);

    // Track isReview
    const isReview = currentStep >= objects.length;

    useEffect(() => {
        currentStepRef.current = currentStep;
    }, [currentStep]);

    useEffect(() => {
        setHide(true);
        const hasPrebuiltJsonGuard = state?.json && !Array.isArray(state.json) && Object.keys(state.json).length > 0;
        if (!state || (state.text === undefined && !hasPrebuiltJsonGuard)) {
            setHide(false);
            navigate('/app');
            return;
        }
        if (state.routeBack) setRouteBack(state.routeBack);
        receivedObjectsEvent.current = false;

        socket.on('progress', onProgress);
        socket.on('actions', onActions);
        socket.on('objects', onObjects);
        socket.on('actionsUpdate', onActionsUpdate);
        socket.on('error', onError);

        // Draft resume: state.json is a CRMObject[] array saved from a previous session
        if (Array.isArray(state.json) && state.json.length > 0 && !textSent) {
            setTextSent(true);
            const restored = state.json.filter(o => o && o.objectType);
            objectsRef.current = restored;
            restored.forEach(o => processedObjectIds.current.add(o.clientObjectId));
            setObjects([...restored]);
            setProcessingData(false);
            logFlowEvent('validate_v2_draft_restored', { objectCount: restored.length });
            return;
        }

        // Direct JSON path: bypass AI when pre-built JSON is provided (test scenarios)
        const hasPrebuiltJson = state.json && !Array.isArray(state.json) && Object.keys(state.json).length > 0;
        if (hasPrebuiltJson && !textSent) {
            setTextSent(true);
            const entityOrder = ['company', 'contact', 'deal', 'task', 'meeting', 'note', 'mail', 'call'];
            const built = [];
            for (const key of entityOrder) {
                if (state.json[key] !== undefined) {
                    const objectType = key === 'mail' ? 'email' : key;
                    try {
                        const normalized = normalizeActionData(objectType, state.json[key]);
                        const obj = buildCRMObjectFromLegacy(objectType, normalized, generateClientObjectId(objectType));
                        // edit-contact entry: lock the contact in edit mode from the start
                        if (editContactLock && objectType === 'contact' && obj.id) {
                            obj.operation = 'update';
                            obj.metadata = { ...(obj.metadata || {}), mode: 'edit', create: false, locked: true };
                            // Snapshot original core values to detect "no change" later.
                            lockedContactSnapshotRef.current = {
                                id: obj.id,
                                clientObjectId: obj.clientObjectId,
                                firstname: normalized.firstname || '',
                                lastname: normalized.lastname || '',
                                email: normalized.email || '',
                                phone: normalized.phone || '',
                            };
                        }
                        built.push(obj);
                    } catch (err) {
                        Sentry.captureException(err, { extra: { objectType, data: state.json[key] } });
                    }
                }
            }
            objectsRef.current = built;
            built.forEach(o => {
                // In edit-contact mode, do NOT pre-mark the contact as processed —
                // the AI's actionsUpdate needs to be able to merge into it.
                if (state.mode === 'edit-contact' && o.objectType === 'contact') return;
                processedObjectIds.current.add(o.clientObjectId);
            });
            setObjects([...built]);
            logFlowEvent('validate_v2_prebuilt_loaded', { objectCount: built.length });
            if (text) sendText(text);
            setProcessingData(false);
            return;
        }

        if (!textSent) {
            setTextSent(true);
            sendText(text);
        }

        return () => {
            socket.off('progress', onProgress);
            socket.off('actions', onActions);
            socket.off('objects', onObjects);
            socket.off('actionsUpdate', onActionsUpdate);
            socket.off('error', onError);
        };
    }, []);

    // ─── Socket Handlers ─────────────────────────────────────────────

    function onProgress(progress) {
        const progressKey = typeof progress === 'string' ? progress : 'processing';
        setTitleProcessing(t(progressKey));
        setProcessingSteps(prev => {
            const updated = prev.map(s => ({ ...s, active: false, done: true }));
            updated.push({ key: progressKey, label: t(progressKey), active: true, done: false });
            return updated;
        });
        logFlowEvent('validate_v2_progress', { progress: progressKey });
    }

    function onActions(actions) {
        if (!actions || typeof actions !== 'object') {
            logFlowEvent('validate_v2_payload_rejected', { eventName: 'actions', reason: 'invalid-payload' });
            return;
        }
        if (receivedObjectsEvent.current) return;
        // Build CRMObject shells from legacy actions format
        const entityOrder = ['company', 'contact', 'deal', 'task', 'meeting', 'note', 'mail', 'call'];
        const shells = [];
        for (const key of entityOrder) {
            if (actions[key] !== undefined) {
                const objectType = key === 'mail' ? 'email' : key;
                shells.push(buildShellCRMObject(objectType));
            }
        }
        objectsRef.current = shells;
        processedObjectIds.current = new Set();
        setObjects([...shells]);
        logFlowEvent('validate_v2_actions_received', { shellCount: shells.length });

        if (oneShot) {
            setProcessingSteps(shells.map(o => ({
                key: o.clientObjectId,
                label: `${t('validating')} ${t(o.objectType)}`,
                active: false,
                done: false,
            })));
        }
    }

    function onObjects(rawObjects) {
        if (!Array.isArray(rawObjects)) {
            logFlowEvent('validate_v2_payload_rejected', { eventName: 'objects', reason: 'non-array' });
            return;
        }
        const shells = rawObjects
            .map((obj, idx) => {
                const objectType = canonicalObjectType(obj?.objectType);
                if (!isSupportedValidationType(objectType)) return null;
                return {
                    ...buildShellCRMObject(objectType),
                    clientObjectId: obj?.clientObjectId || generateClientObjectId(objectType, idx),
                };
            })
            .filter(Boolean);
        if (shells.length === 0) return;
        receivedObjectsEvent.current = true;
        objectsRef.current = shells;
        processedObjectIds.current = new Set();
        setObjects([...shells]);
        logFlowEvent('validate_v2_objects_received', { shellCount: shells.length });
        if (oneShot) {
            setProcessingSteps(shells.map(o => ({
                key: o.clientObjectId,
                label: `${t('validating')} ${t(o.objectType)}`,
                active: false,
                done: false,
            })));
        }
    }

    // Edit-contact lock: detect if the AI-merged contact has any new core data
    // beyond the original snapshot. If not, the user dictated something else
    // (task/note/etc) and the contact step should be skipped entirely.
    const lockedContactHasChanges = (mergedContact) => {
        const snap = lockedContactSnapshotRef.current;
        if (!snap) return true;
        const norm = (v) => (v || '').toString().trim().toLowerCase();
        const get = (name) => {
            const p = (mergedContact.properties || []).find(pp => pp.name === name);
            return p ? p.value : '';
        };
        const coreChanged = ['firstname', 'lastname', 'email', 'phone']
            .some(f => norm(get(f)) && norm(get(f)) !== norm(snap[f]));
        const customChanged = (mergedContact.properties || [])
            .some(p => p.core === false && p.value !== '' && p.value != null);
        return coreChanged || customChanged;
    };

    function onActionsUpdate(crmObjectJSON) {
        console.log('[ValidateV2] onActionsUpdate received:', JSON.stringify(crmObjectJSON, null, 2));
        if (!crmObjectJSON || typeof crmObjectJSON !== 'object') {
            logFlowEvent('validate_v2_payload_rejected', { eventName: 'actionsUpdate', reason: 'invalid-payload' });
            return;
        }

        // Support both CRMObject JSON (new) and legacy {type, data} (backward compat)
        let objectType, payloadObjectId, mergedObj;
        if (crmObjectJSON.objectType && Array.isArray(crmObjectJSON.properties)) {
            // New CRMObject format from backend
            objectType = canonicalObjectType(crmObjectJSON.objectType);
            payloadObjectId = crmObjectJSON.clientObjectId || null;
            mergedObj = crmObjectJSON;
        } else if (crmObjectJSON.type) {
            // Legacy format: {type, data, clientObjectId}
            objectType = canonicalObjectType(crmObjectJSON.type);
            payloadObjectId = crmObjectJSON.clientObjectId || crmObjectJSON.objectId || null;
            const data = crmObjectJSON.data && typeof crmObjectJSON.data === 'object' ? crmObjectJSON.data : {};
            mergedObj = buildCRMObjectFromLegacy(objectType, normalizeActionData(objectType, data), payloadObjectId || generateClientObjectId(objectType));
        } else {
            logFlowEvent('validate_v2_payload_rejected', { eventName: 'actionsUpdate', reason: 'unrecognized-format' });
            return;
        }

        if (!isSupportedValidationType(objectType)) {
            logFlowEvent('validate_v2_payload_rejected', { eventName: 'actionsUpdate', reason: 'unknown-type', objectType });
            return;
        }

        // Find matching shell and replace with full CRMObject data
        const current = objectsRef.current;
        const payloadHubspotId = mergedObj.id || (crmObjectJSON.data && crmObjectJSON.data.id) || null;
        let idx = payloadObjectId
            ? current.findIndex(o => o.clientObjectId === payloadObjectId)
            : current.findIndex(o => o.objectType === objectType && !processedObjectIds.current.has(o.clientObjectId));
        // Also match by HubSpot recordId if clientObjectId match failed
        if (idx === -1 && payloadHubspotId) {
            idx = current.findIndex(o => o.objectType === objectType && o.id === payloadHubspotId);
        }
        let targetObjectId = payloadObjectId;
        if (idx === -1) {
            // New entity not in initial skeleton — add it
            if (!mergedObj.clientObjectId) mergedObj.clientObjectId = generateClientObjectId(objectType);
            current.push(mergedObj);
            targetObjectId = mergedObj.clientObjectId;
        } else {
            // Replace shell with full CRMObject, keep shell's clientObjectId
            const existing = current[idx] || {};
            const existingId = existing.clientObjectId || payloadObjectId || generateClientObjectId(objectType);
            mergedObj.clientObjectId = existingId;
            // Preserve UI-side metadata (pipeline/stage/etc.) when backend payload omits or nullifies fields
            const existingMeta = existing.metadata || {};
            const incomingMeta = mergedObj.metadata || {};
            const mergedMeta = { ...existingMeta };
            for (const [k, v] of Object.entries(incomingMeta)) {
                if (v != null && !(Array.isArray(v) && v.length === 0)) mergedMeta[k] = v;
            }
            // Deal-specific: keep existing pipeline.stages array (backend payload omits it)
            if (objectType === 'deal') {
                if (mergedMeta.pipeline && existingMeta.pipeline?.stages?.length > 0
                    && mergedMeta.pipeline.id === existingMeta.pipeline.id
                    && !mergedMeta.pipeline.stages?.length) {
                    mergedMeta.pipeline = { ...mergedMeta.pipeline, stages: existingMeta.pipeline.stages };
                }
            }
            mergedObj.metadata = mergedMeta;
            // edit-contact lock: AI must NEVER flip a locked contact away from edit.
            // Preserve the resolved id, force operation='update', restore mode='edit'.
            // If the AI extracted nothing new for the contact, drop the step.
            if (editContactLock && objectType === 'contact' && existing.metadata?.locked) {
                mergedObj.id = existing.id || mergedObj.id;
                mergedObj.operation = 'update';
                mergedObj.metadata = { ...mergedObj.metadata, mode: 'edit', create: false, locked: true };
                if (!lockedContactHasChanges(mergedObj)) {
                    logFlowEvent('validate_v2_locked_contact_skipped', { reason: 'no-changes-detected' });
                    current.splice(idx, 1);
                    objectsRef.current = [...current];
                    setObjects([...current]);
                    if (targetObjectId) processedObjectIds.current.add(targetObjectId);
                    if (!oneShot && current.length > 0) {
                        setLoading(false);
                        setProcessingData(false);
                    } else if (current.length === 0) {
                        setLoading(false);
                        setProcessingData(false);
                    }
                    return;
                }
            }
            current[idx] = mergedObj;
            targetObjectId = existingId;
        }
        if (targetObjectId) processedObjectIds.current.add(targetObjectId);

        if (targetObjectId && !aiSnapshotsRef.current.has(targetObjectId)) {
            try {
                aiSnapshotsRef.current.set(targetObjectId, structuredClone(mergedObj));
            } catch (_err) {
                aiSnapshotsRef.current.set(targetObjectId, JSON.parse(JSON.stringify(mergedObj)));
            }
        }

        objectsRef.current = [...current];
        setObjects([...current]);
        logFlowEvent('validate_v2_actions_update_received', {
            objectType,
            clientObjectId: targetObjectId,
            objectCount: current.length,
        });

        // Update processing steps
        if (oneShot) {
            setProcessingSteps(prev => prev.map(s =>
                (s.key === targetObjectId || s.key === objectType) ? { ...s, done: true, active: false } : s
            ));
        }

        // Check if all entities are processed
        const allProcessed = current.every(o => processedObjectIds.current.has(o.clientObjectId));

        if (oneShot && allProcessed && !oneShotChecked.current) {
            oneShotChecked.current = true;
            if (isOneShotReady(current)) {
                setOneShotMode(true);
                setCurrentStep(current.length); // Go to review
            } else {
                setCurrentStep(0);
            }
            setLoading(false);
            setProcessingData(false);
            return;
        }

        // Normal mode: show current step when its data is ready
        if (!oneShot && idx === currentStepRef.current) {
            setLoading(false);
            setProcessingData(false);
        }
    }

    function onError(err) {
        Sentry.captureException(err);
        logFlowEvent('validate_v2_socket_error', { message: err?.message, textPresent: Boolean(err?.text) });
        setError(true);
        setProcessingData(false);
        if (err?.text) {
            setHide(false);
            navigate('/app', { state: { text: err.text } });
        }
    }

    // ─── Send ────────────────────────────────────────────────────────

    function sendText(text) {
        setProcessingData(true);
        logFlowEvent('validate_v2_socket_emit_textv2', { textLength: text?.length || 0, oneShot });
        const emitJson = (state && state.json && !Array.isArray(state.json)) ? state.json : {};
        const emitMode = (state && state.mode) ? state.mode : null;
        const payload = { data: { text, json: emitJson, mode: emitMode, language: getLanguage(), timezone: getUserTimeZone(), ocrText, flowId: flowIdRef.current } };
        if (socket.connected) {
            socket.emit('textV2', payload);
        } else {
            socket.once('connect', () => socket.emit('textV2', payload));
            socket.connect();
        }
    }

    // ─── Submit ──────────────────────────────────────────────────────

    function submitObjects() {
        setProcessingData(true);
        setTitleProcessing(t('sendingData'));
        disconnectSocket();

        const compactObjects = compactObjectsForSubmit(objectsRef.current);
        logFlowEvent('validate_v2_submit_payload_compacted', {
            objectCount: compactObjects.length,
            approxBytes: getApproxJsonBytes(compactObjects),
        });
        api.SubmitCRMObjects(compactObjects, { flowId: flowIdRef.current }).then((result) => {
            clearDraft();
            try {
                const corrections = computeCorrectionsFromSnapshots(objectsRef.current, aiSnapshotsRef.current, state?.text || '');
                if (corrections.length > 0) {
                    api.SubmitAudioCorrections(corrections);
                }
            } catch (correctionErr) {
                Sentry.captureException(correctionErr);
            }
            let message;
            if (result.errors?.length > 0) {
                message = t('notObjectProcessed');
                result.errors.forEach(err => { message += t(err.toLowerCase()) + '\n'; });
            }
            const createdEntities = (result.created || []).map(e => ({
                type: e.type, name: e.name, id: e.id,
            }));
            if (createdEntities.length === 0) {
                objectsRef.current.forEach(obj => {
                    createdEntities.push({ type: obj.objectType, name: obj.displayName || t(obj.objectType) });
                });
            }
            setProcessingData(false);
            setHide(false);
            logFlowEvent('validate_v2_submit_succeeded', { createdCount: createdEntities.length, errorCount: result.errors?.length || 0 });
            navigate(routeBack, { state: { ok: true, fail: result.errors?.length > 0, message, createdEntities } });
        }).catch((err) => {
            setProcessingData(false);
            setLoading(false);
            setNextDisabled(false);
            Sentry.captureException(err);
            logFlowEvent('validate_v2_submit_failed', { message: err?.message, hasErrorObject: Boolean(err) });
            setCurrentStep(0);
        });
    }

    function disconnectSocket() {
        socket.off('progress', onProgress);
        socket.off('actions', onActions);
        socket.off('objects', onObjects);
        socket.off('actionsUpdate', onActionsUpdate);
        socket.off('error', onError);
    }

    // ─── Navigation ──────────────────────────────────────────────────

    function goToStep(idx) {
        const target = objectsRef.current[idx];
        const isReviewStep = idx >= objectsRef.current.length;
        if (target && !isReviewStep && !processedObjectIds.current.has(target.clientObjectId)) {
            setTitleProcessing(t('processing'));
            setProcessingData(true);
        }
        setCurrentStep(idx);
        setLoading(false);
        setNextDisabled(false);
    }

    function goNext() {
        saveDraft(objectsRef.current, text, []);

        // Cascade resolved contact to email/meeting recipients
        const contactObj = objectsRef.current.find(o => o.objectType === 'contact' && o.id);
        if (contactObj) {
            const contactData = {
                id: contactObj.id,
                firstname: (contactObj.properties || []).find(p => p.name === 'firstname')?.value || '',
                lastname: (contactObj.properties || []).find(p => p.name === 'lastname')?.value || '',
                email: (contactObj.properties || []).find(p => p.name === 'email')?.value || '',
            };
            for (const obj of objectsRef.current) {
                if (obj.objectType === 'email') {
                    const recipients = (obj.metadata?.recipient || []).filter(r => r && r.id);
                    if (!recipients.some(r => r.id === contactData.id)) {
                        obj.metadata = { ...obj.metadata, recipient: [...recipients, contactData] };
                    }
                }
                if (obj.objectType === 'meeting') {
                    const attendees = obj.metadata?.attendees || [];
                    if (!attendees.some(a => a.id === contactData.id)) {
                        obj.metadata = { ...obj.metadata, attendees: [...attendees, contactData] };
                    }
                }
            }
            setObjects([...objectsRef.current]);
        }

        if (isReview) {
            submitObjects();
        } else if (currentStep + 1 <= objects.length) {
            goToStep(currentStep + 1);
        }
    }

    function goBack() {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setNextDisabled(false);
            setRenderKey(k => k + 1);
        }
    }

    function skipStep() {
        const objToRemove = objects[currentStep];
        if (objToRemove) {
            const filtered = objectsRef.current.filter(o => o.clientObjectId !== objToRemove.clientObjectId);
            objectsRef.current = filtered;
            setObjects(filtered);
            Sentry.captureMessage(`User skipped V2 step: ${objToRemove.objectType}`);
        }
        setNextDisabled(false);
        // currentStep now points to the next object (since array shifted)
    }

    function editStep(idx) {
        setOneShotMode(false);
        goToStep(idx);
        setRenderKey(k => k + 1);
    }

    function addEntity(objectType) {
        const newObj = buildCRMObjectFromLegacy(objectType, {}, generateClientObjectId(objectType));
        objectsRef.current.push(newObj);
        setObjects([...objectsRef.current]);
        setCurrentStep(objectsRef.current.length - 1);
        setNextDisabled(false);
    }

    // ─── Object Update ───────────────────────────────────────────────

    const updateObject = useCallback((updatedObj) => {
        const idx = objectsRef.current.findIndex(o => o.clientObjectId === updatedObj.clientObjectId);
        if (idx >= 0) {
            objectsRef.current[idx] = updatedObj;
            setObjects([...objectsRef.current]);
        }
    }, []);

    // ─── Helpers ─────────────────────────────────────────────────────

    function isOneShotReady(objs) {
        for (const obj of objs) {
            const mainProps = (obj.properties || []).filter(p => p.core && p.group === 'main' && p.mandatory);
            for (const p of mainProps) {
                if (!p.value || (typeof p.value === 'string' && !p.value.trim())) return false;
            }
            if (obj.objectType === 'deal' && (!obj.metadata?.pipeline?.id || !obj.metadata?.stage?.id)) return false;
        }
        return true;
    }

    // ─── Current step info ───────────────────────────────────────────

    const currentObj = isReview ? null : objects[currentStep];
    const isCurrentEdit = currentObj?.operation === 'update' && Boolean(currentObj?.id);
    const stepLabel = isReview
        ? t('review')
        : (currentObj
            ? (isCurrentEdit
                ? `${t('editing')} · ${t(currentObj.objectType)}`
                : t(`validate${currentObj.objectType.charAt(0).toUpperCase() + currentObj.objectType.slice(1)}`))
            : '');
    const hasAnyUpdate = objects.some(o => o?.operation === 'update' && Boolean(o?.id));
    const stepIcon = isReview ? ENTITY_ICONS.REVIEW : (currentObj ? ENTITY_ICONS[currentObj.objectType] : null);
    const totalSteps = objects.length + 1; // +1 for review

    // ─── Render ──────────────────────────────────────────────────────

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <Box sx={{ flex: 1, overflowY: 'auto', padding: '1.25rem', paddingBottom: '160px' }}>
                    {processingData && (
                        <div data-testid="processing-indicator" style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10 }}>
                            <ProcessingComponent processingText={titleProcessing} steps={processingSteps} />
                        </div>
                    )}

                    {!processingData && objects.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                {currentStep > 0 && (
                                    <IconButton
                                        data-testid="validate-back-button"
                                        onClick={goBack}
                                        sx={{ color: 'white', minWidth: 44, minHeight: 44 }}
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                )}
                                <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            {stepIcon}
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                                                {t('step') || 'Step'} {currentStep + 1} / {totalSteps}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="caption" sx={{ color: '#5ec2c2', fontWeight: 600 }}>
                                            {isReview ? t('review') : t(currentObj?.objectType)}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="center" spacing={0.75} sx={{ mt: 1 }}>
                                        {[...objects, { _review: true }].map((_, i) => {
                                            const isActive = i === currentStep;
                                            const isPast = i < currentStep;
                                            return (
                                                <Box
                                                    key={i}
                                                    sx={{
                                                        width: isActive ? 32 : 10, height: 10, borderRadius: 5,
                                                        backgroundColor: isActive ? '#1f7a7a' : isPast ? 'rgba(31,122,122,0.6)' : 'rgba(255,255,255,0.2)',
                                                        boxShadow: isActive ? '0 0 8px rgba(31,122,122,0.5)' : 'none',
                                                        transition: 'all 0.3s ease',
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                    )}

                    <Fade in={!processingData}>
                        <Box sx={{ width: '100%' }}>
                            <Stack spacing={3} alignItems="center" sx={{ width: '100%' }}>
                                <Typography data-testid="validate-form-title" variant="h5" gutterBottom align="center" sx={{ color: TEXT_PRIMARY }}>
                                    {stepLabel}
                                </Typography>

                                <ValidationErrorBoundary
                                    key={`${currentObj?.clientObjectId || currentObj?.objectType || 'review'}-${currentStep}-${renderKey}`}
                                    onSkip={skipStep}
                                    errorMessage={t('validationStepError')}
                                    skipLabel={t('skipStep')}
                                    retryLabel={t('retry')}
                                >
                                    {!isReview && currentObj && (
                                        <CRMObjectForm
                                            crmObject={currentObj}
                                            onChange={updateObject}
                                            setNextDisabled={setNextDisabled}
                                            loaded={() => { setLoading(false); }}
                                            showGoogleMeet={user?.gmail ?? false}
                                            lockedMode={editContactLock && currentObj.objectType === 'contact'}
                                        />
                                    )}

                                    {isReview && (
                                        <ReviewStepV2
                                            objects={objects}
                                            oneShotMode={oneShotMode}
                                            loaded={() => { setLoading(false); setNextDisabled(false); }}
                                            onEditStep={editStep}
                                            onAddEntity={addEntity}
                                        />
                                    )}
                                </ValidationErrorBoundary>
                            </Stack>
                        </Box>
                    </Fade>
                </Box>

                {/* Sticky bottom bar */}
                {!processingData && (
                    <Box
                        sx={{
                            position: 'fixed', bottom: 0, left: 0, right: 0,
                            backgroundColor: 'rgba(18, 18, 30, 0.85)',
                            backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                            borderTop: '1px solid rgba(255,255,255,0.08)',
                            padding: '12px 16px',
                            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                            zIndex: 1200,
                        }}
                    >
                        <Stack direction="column" spacing={1.5}>
                            <Box sx={{ position: 'relative' }}>
                                <Button
                                    data-testid="next-button"
                                    disabled={loading || nextDisabled}
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    sx={{
                                        minHeight: 52, fontWeight: 700, fontSize: '1rem',
                                        borderRadius: '14px', textTransform: 'uppercase', letterSpacing: '0.5px',
                                        '&.Mui-disabled': { backgroundColor: 'rgba(31,122,122,0.25)', color: 'rgba(255,255,255,0.35)' },
                                    }}
                                    onClick={goNext}
                                >
                                    {isReview ? (hasAnyUpdate ? t('saveChanges') : t('confirmAndSend')) : (currentStep < objects.length - 1 ? t('next') : t('review'))}
                                    {loading && (
                                        <CircularProgress
                                            size={24}
                                            sx={{
                                                color: 'white', position: 'absolute',
                                                top: '50%', right: '5%', marginTop: '-12px', marginLeft: '-12px',
                                            }}
                                        />
                                    )}
                                </Button>
                            </Box>
                            <Button
                                data-testid="cancel-button"
                                variant="text"
                                color="error"
                                sx={{ minHeight: 44, fontSize: '0.85rem', borderRadius: '12px' }}
                                onClick={() => setOpen(true)}
                            >
                                {t('cancel')}
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Box>

            <Dialog disableEscapeKeyDown open={open} onClose={() => setOpen(false)}
                slotProps={{ paper: { sx: DIALOG_PAPER } }}>
                <DialogTitle sx={{ color: TEXT_PRIMARY, display: 'flex', alignItems: 'center', gap: 1, pb: 0 }}>
                    <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
                    {t('discardChanges')}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: TEXT_SECONDARY, mt: 1 }}>{t('cancelConfirmation')}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        data-testid="cancel-confirm-stay"
                        variant="contained"
                        onClick={() => setOpen(false)}
                        sx={{
                            backgroundColor: ACCENT, color: '#fff', minHeight: 44,
                            borderRadius: '12px', fontWeight: 600, flex: 1,
                            '&:hover': { backgroundColor: '#5e9496' },
                        }}
                    >
                        {t('stay')}
                    </Button>
                    <Button
                        data-testid="cancel-confirm-ok"
                        variant="outlined"
                        color="error"
                        onClick={() => {
                            clearDraft();
                            disconnectSocket();
                            setHide(false);
                            navigate(routeBack);
                        }}
                        sx={{ minHeight: 44, borderRadius: '12px', fontWeight: 600, flex: 1 }}
                    >
                        {t('discard')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={error}
                disableEscapeKeyDown
                onClose={() => {
                    setError(false);
                    disconnectSocket();
                    setHide(false);
                    navigate(routeBack);
                }}
                slotProps={{ paper: { sx: DIALOG_PAPER } }}
            >
                <DialogTitle sx={{ color: TEXT_PRIMARY }}>{t('errorProcessingRequest')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: TEXT_SECONDARY }}>{t('errorProcessingRequestDescription')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        disconnectSocket();
                        setHide(false);
                        navigate(routeBack);
                    }} sx={{ color: ACCENT }}>{t('ok')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

// ─── Helpers ─────────────────────────────────────────────────────────

/**
 * Build a CRMObject from legacy action data.
 * This is a frontend-only conversion used during the transition.
 */
function buildCRMObjectFromLegacy(objectType, data, clientObjectId) {
    const safeData = data && typeof data === 'object' ? data : {};
    const CORE_PROPS = {
        deal: [
            { name: 'dealname', labelKey: 'dealName', type: 'string', fieldType: 'text', mandatory: true, group: 'main', core: true },
            { name: 'amount', labelKey: 'amount', type: 'number', fieldType: 'number', mandatory: false, group: 'main', core: true },
            { name: 'closedate', labelKey: 'estimatedCloseDate', type: 'date', fieldType: 'date', mandatory: false, group: 'main', core: true },
        ],
        contact: [
            { name: 'firstname', labelKey: 'firstName', type: 'string', fieldType: 'text', mandatory: true, group: 'main', core: true },
            { name: 'lastname', labelKey: 'lastName', type: 'string', fieldType: 'text', mandatory: false, group: 'main', core: true },
            { name: 'email', labelKey: 'email', type: 'string', fieldType: 'text', mandatory: false, group: 'main', core: true },
            { name: 'phone', labelKey: 'phone', type: 'string', fieldType: 'text', mandatory: false, group: 'main', core: true },
        ],
        company: [
            { name: 'name', labelKey: 'companyName', type: 'string', fieldType: 'text', mandatory: true, group: 'main', core: true },
            { name: 'domain', labelKey: 'domain', type: 'string', fieldType: 'text', mandatory: false, group: 'main', core: true },
        ],
        task: [
            { name: 'subject', labelKey: 'subject', type: 'string', fieldType: 'text', mandatory: true, group: 'main', core: true },
            { name: 'hs_task_status', labelKey: 'status', type: 'enumeration', fieldType: 'select', mandatory: false, group: 'main', core: true, options: [{ label: 'NOT_STARTED', value: 'NOT_STARTED' }, { label: 'COMPLETED', value: 'COMPLETED' }] },
            { name: 'hs_task_priority', labelKey: 'priority', type: 'enumeration', fieldType: 'select', mandatory: false, group: 'main', core: true, options: [{ label: 'LOW', value: 'LOW' }, { label: 'MEDIUM', value: 'MEDIUM' }, { label: 'HIGH', value: 'HIGH' }] },
            { name: 'hs_timestamp', labelKey: 'dueDate', type: 'datetime', fieldType: 'date', mandatory: false, group: 'main', core: true },
            { name: 'hs_task_type', labelKey: 'taskType', type: 'enumeration', fieldType: 'radio', mandatory: false, group: 'main', core: true, options: [{ label: 'EMAIL', value: 'EMAIL' }, { label: 'CALL', value: 'CALL' }, { label: 'TODO', value: 'TODO' }] },
            { name: 'description', labelKey: 'description', type: 'string', fieldType: 'textarea', mandatory: false, group: 'details', core: true },
        ],
        meeting: [
            { name: 'title', labelKey: 'meetingTitle', type: 'string', fieldType: 'text', mandatory: true, group: 'main', core: true },
            { name: 'startDate', labelKey: 'startDate', type: 'datetime', fieldType: 'date', mandatory: false, group: 'main', core: true },
            { name: 'endTime', labelKey: 'endTime', type: 'datetime', fieldType: 'date', mandatory: false, group: 'main', core: true },
            { name: 'location', labelKey: 'location', type: 'string', fieldType: 'text', mandatory: false, group: 'details', core: true },
            { name: 'description', labelKey: 'description', type: 'string', fieldType: 'textarea', mandatory: false, group: 'details', core: true },
            { name: 'internalNote', labelKey: 'internalNote', type: 'string', fieldType: 'textarea', mandatory: false, group: 'internal', core: true },
        ],
        note: [
            { name: 'note', labelKey: 'note', type: 'string', fieldType: 'textarea', mandatory: true, group: 'main', core: true },
        ],
        email: [
            { name: 'subject', labelKey: 'emailSubject', type: 'string', fieldType: 'text', mandatory: true, group: 'main', core: true },
            { name: 'message', labelKey: 'emailMessage', type: 'string', fieldType: 'textarea', mandatory: true, group: 'main', core: true },
            { name: 'sender', labelKey: 'sender', type: 'string', fieldType: 'text', mandatory: false, group: 'main', core: true },
        ],
        call: [
            { name: 'title', labelKey: 'callTitle', type: 'string', fieldType: 'text', mandatory: true, group: 'main', core: true },
            { name: 'date', labelKey: 'date', type: 'datetime', fieldType: 'date', mandatory: false, group: 'main', core: true },
            { name: 'duration', labelKey: 'duration', type: 'number', fieldType: 'number', mandatory: false, group: 'main', core: true },
            { name: 'direction', labelKey: 'direction', type: 'enumeration', fieldType: 'radio', mandatory: false, group: 'main', core: true, options: [{ label: 'INBOUND', value: 'INBOUND' }, { label: 'OUTBOUND', value: 'OUTBOUND' }] },
            { name: 'description', labelKey: 'description', type: 'string', fieldType: 'textarea', mandatory: false, group: 'details', core: true },
        ],
    };

    // Legacy field name → canonical property name
    const LEGACY_MAP = {
        deal: { dealname: 'dealname', amount: 'amount', estimatedDateClosed: 'closedate' },
        task: { subject: 'subject', status: 'hs_task_status', priority: 'hs_task_priority', dueDate: 'hs_timestamp', hs_task_type: 'hs_task_type', description: 'description' },
        contact: { firstname: 'firstname', lastname: 'lastname', email: 'email', phone: 'phone' },
        company: { name: 'name', domain: 'domain' },
        meeting: { title: 'title', startDate: 'startDate', endTime: 'endTime', location: 'location', description: 'description', internalNote: 'internalNote' },
        note: { note: 'note' },
        email: { subject: 'subject', message: 'message', sender: 'sender' },
        call: { title: 'title', date: 'date', duration: 'duration', direction: 'direction', description: 'description' },
    };

    const coreDefs = CORE_PROPS[objectType] || [];
    const legacyMap = LEGACY_MAP[objectType] || {};

    // Build properties with values from legacy data
    const properties = coreDefs.map(def => {
        let value;
        // Try legacy key reverse lookup
        const reverseEntry = Object.entries(legacyMap).find(([, canonical]) => canonical === def.name);
        if (reverseEntry) value = safeData[reverseEntry[0]];
        if (value === undefined) value = safeData[def.name];
        return { ...def, value: value !== undefined ? value : undefined };
    });

    // Add custom properties
    if (Array.isArray(safeData.properties)) {
        const coreNames = new Set(coreDefs.map(d => d.name));
        for (const p of safeData.properties) {
            if (!coreNames.has(p.name) && shouldKeepCustomProperty(p)) {
                properties.push({ ...p, core: false, group: 'custom' });
            }
        }
    }

    // Build metadata
    const metadata = {};
    if (objectType === 'deal') {
        metadata.pipeline = safeData.pipeline || null;
        metadata.stage = safeData.stage || null;
    }
    if (objectType === 'contact' || objectType === 'company') {
        metadata.create = safeData.create !== undefined ? safeData.create : true;
    }
    if (objectType === 'meeting') {
        metadata.createMeeting = safeData.createMeeting || false;
        metadata.createGoogleMeet = safeData.createGoogleMeet || false;
        metadata.attendees = safeData.attendees || [];
    }
    if (objectType === 'email') {
        metadata.recipient = safeData.recipient || [];
        metadata.cc = safeData.cc || [];
        metadata.bcc = safeData.bcc || [];
        metadata.attachments = safeData.attachments || [];
    }
    if (objectType === 'call') {
        metadata.direction = safeData.direction || 'OUTBOUND';
    }

    // Build display name
    let displayName = '';
    switch (objectType) {
        case 'deal': displayName = safeData.dealname || safeData.name || ''; break;
        case 'contact': displayName = `${safeData.firstname || ''} ${safeData.lastname || ''}`.trim(); break;
        case 'company': displayName = safeData.name || ''; break;
        case 'task': displayName = safeData.subject || ''; break;
        case 'meeting': displayName = safeData.title || ''; break;
        case 'note': displayName = (safeData.note || '').substring(0, 50); break;
        case 'email': displayName = safeData.subject || ''; break;
        case 'call': displayName = safeData.title || ''; break;
    }

    return {
        clientObjectId: clientObjectId || safeData.clientObjectId || generateClientObjectId(objectType),
        objectType,
        operation: safeData.id ? 'update' : 'create',
        id: safeData.id || null,
        displayName,
        properties,
        associations: [],
        metadata,
        options: safeData.options || [],
    };
}

function normalizeActionData(objectType, data) {
    if (!data || typeof data !== 'object') return {};
    const d = { ...data };
    if (objectType === 'task') {
        if (!d.subject && d.title) d.subject = d.title;
        if (!d.dueDate && d.due_date) d.dueDate = d.due_date;
        if (!d.dueDate) d.dueDate = new Date().toISOString();
        if (!d.priority) d.priority = 'NONE';
        if (!d.status) d.status = 'NOT_STARTED';
        if (d.status === 'pending' || d.status === 'in_progress') d.status = 'NOT_STARTED';
        if (d.status === 'complete' || d.status === 'done') d.status = 'COMPLETED';
    }
    if (objectType === 'note') {
        if (!d.note && d.content) d.note = d.content;
        if (!d.note && d.hs_note_body) d.note = d.hs_note_body;
    }
    if (objectType === 'meeting') {
        if (!d.startDate && d.start_date) d.startDate = d.start_date;
        if (!d.title && d.hs_meeting_title) d.title = d.hs_meeting_title;
        if (!d.startDate) d.startDate = new Date().toISOString();
        if (!d.endTime) d.endTime = new Date(Date.now() + 3600000).toISOString();
    }
    if (objectType === 'call') {
        if (!d.title && d.hs_call_title) d.title = d.hs_call_title;
        if (!d.date) d.date = new Date().toISOString();
    }
    return d;
}

function buildShellCRMObject(objectType) {
    return {
        clientObjectId: generateClientObjectId(objectType),
        objectType,
        operation: 'create',
        id: null,
        displayName: '',
        properties: [],
        associations: [],
        metadata: {},
        options: [],
    };
}

function isSupportedValidationType(objectType) {
    return ['company', 'contact', 'deal', 'task', 'meeting', 'note', 'email', 'call'].includes(objectType);
}

function generateFlowId() {
    return `flow-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function generateClientObjectId(objectType, index = 0) {
    return `${objectType}-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
}

function compactObjectsForSubmit(objects) {
    if (!Array.isArray(objects)) return [];
    return objects
        .map(compactObjectForSubmit)
        .filter(Boolean);
}

function compactObjectForSubmit(obj) {
    if (!obj || typeof obj !== 'object' || !obj.objectType) return null;
    return {
        clientObjectId: obj.clientObjectId || null,
        objectType: obj.objectType,
        operation: obj.operation || 'create',
        id: obj.id || null,
        displayName: obj.displayName || '',
        properties: compactPropertiesForSubmit(obj.properties),
        associations: compactAssociationsForSubmit(obj.associations),
        metadata: compactMetadataForSubmit(obj.objectType, obj.metadata),
        options: [],
    };
}

function compactPropertiesForSubmit(properties) {
    if (!Array.isArray(properties)) return [];
    const deduped = new Map();
    for (const prop of properties) {
        if (!prop || typeof prop !== 'object') continue;
        const name = typeof prop.name === 'string' ? prop.name.trim() : '';
        if (!name || !isAllowlistedProperty(prop) || !hasMeaningfulValue(prop.value)) continue;
        const compact = { name, value: prop.value };
        if (prop.core === false) compact.core = false;
        if (typeof prop.error === 'string' && prop.error.trim()) compact.error = prop.error;
        deduped.set(name, compact);
    }
    return Array.from(deduped.values());
}

function compactAssociationsForSubmit(associations) {
    if (!Array.isArray(associations)) return [];
    return associations
        .map((assoc) => {
            if (!assoc || typeof assoc !== 'object') return null;
            const id = assoc.id != null ? String(assoc.id) : '';
            const objectType = typeof assoc.objectType === 'string' ? assoc.objectType : '';
            if (!id || !objectType) return null;
            return {
                id,
                objectType,
                displayName: typeof assoc.displayName === 'string' ? assoc.displayName : '',
            };
        })
        .filter(Boolean);
}

function compactMetadataForSubmit(objectType, metadata) {
    const safeMetadata = metadata && typeof metadata === 'object' ? metadata : {};
    switch (objectType) {
        case 'deal':
            return {
                pipeline: compactEntityRef(safeMetadata.pipeline),
                stage: compactEntityRef(safeMetadata.stage),
            };
        case 'contact':
        case 'company':
            return {
                create: safeMetadata.create !== false,
            };
        case 'meeting':
            return {
                createMeeting: Boolean(safeMetadata.createMeeting),
                createGoogleMeet: Boolean(safeMetadata.createGoogleMeet),
                attendees: compactPeopleList(safeMetadata.attendees),
            };
        case 'email':
            return {
                recipient: compactPeopleList(safeMetadata.recipient),
                cc: compactPeopleList(safeMetadata.cc),
                bcc: compactPeopleList(safeMetadata.bcc),
                attachments: compactAttachmentsList(safeMetadata.attachments),
            };
        case 'call':
            return {
                direction: safeMetadata.direction || 'OUTBOUND',
            };
        default:
            return {};
    }
}

function compactPeopleList(list) {
    if (!Array.isArray(list)) return [];
    const deduped = new Map();
    for (const person of list) {
        if (!person || typeof person !== 'object') continue;
        const id = person.id != null ? String(person.id) : '';
        const email = typeof person.email === 'string' ? person.email.trim() : '';
        const key = id || email;
        if (!key) continue;
        deduped.set(key, {
            id: id || undefined,
            firstname: typeof person.firstname === 'string' ? person.firstname : '',
            lastname: typeof person.lastname === 'string' ? person.lastname : '',
            email: email || undefined,
            phone: typeof person.phone === 'string' ? person.phone : '',
        });
    }
    return Array.from(deduped.values());
}

function compactAttachmentsList(list) {
    if (!Array.isArray(list)) return [];
    const deduped = new Set();
    for (const item of list) {
        if (typeof item === 'string' && item.trim()) {
            deduped.add(item.trim());
            continue;
        }
        if (item && typeof item === 'object') {
            const id = item.attachmentId || item.id;
            if (id) deduped.add(String(id));
        }
    }
    return Array.from(deduped.values());
}

function compactEntityRef(value) {
    if (!value || typeof value !== 'object') return null;
    const id = value.id != null ? String(value.id) : null;
    if (!id) return null;
    return {
        id,
        label: typeof value.label === 'string' ? value.label : (typeof value.name === 'string' ? value.name : undefined),
    };
}

function hasMeaningfulValue(value) {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
}

function computeCorrectionsFromSnapshots(finalObjects, snapshots, transcript) {
    const corrections = [];
    if (!Array.isArray(finalObjects) || !snapshots || snapshots.size === 0) return corrections;
    const safeTranscript = typeof transcript === 'string' ? transcript : '';
    for (const obj of finalObjects) {
        if (!obj || !obj.clientObjectId) continue;
        const snapshot = snapshots.get(obj.clientObjectId);
        if (!snapshot) continue;
        const snapshotProps = Array.isArray(snapshot.properties) ? snapshot.properties : [];
        const aiByName = new Map();
        for (const sp of snapshotProps) {
            if (sp && typeof sp.name === 'string') aiByName.set(sp.name, sp.value);
        }
        const finalProps = Array.isArray(obj.properties) ? obj.properties : [];
        for (const prop of finalProps) {
            if (!prop || !prop.core || typeof prop.name !== 'string') continue;
            if (!hasMeaningfulValue(prop.value)) continue;
            const aiValue = aiByName.has(prop.name) ? aiByName.get(prop.name) : null;
            if (correctionEquivalent(aiValue, prop.value)) continue;
            corrections.push({
                transcript: safeTranscript,
                entityType: obj.objectType,
                field: prop.name,
                aiValue: aiValue == null ? null : aiValue,
                finalValue: prop.value,
            });
        }
    }
    return corrections;
}

function correctionEquivalent(a, b) {
    if (a === b) return true;
    if (a == null && (b === '' || b == null)) return true;
    if (b == null && (a === '' || a == null)) return true;
    if (typeof a === 'string' && typeof b === 'string') {
        return a.trim() === b.trim();
    }
    return false;
}

function getApproxJsonBytes(value) {
    try {
        return new TextEncoder().encode(JSON.stringify(value || null)).length;
    } catch (_error) {
        return -1;
    }
}

export default ValidateV2;
